<template>
	<transition name="fade" mode="out-in">
		<section :key="authStore.mfa.multifactorToken" class="social-login__guts">
			<mfa-login-process
				v-if="authStore.mfa.multifactorToken"
				:remember-me="emailRememberMe"
				:invite-code="inviteCode"
				:authentication-provider="authenticationProvider"
			/>
			<device-verification-login-process
				v-else-if="authStore.deviceVerification.token"
				:remember-me="emailRememberMe"
				:invite-code="inviteCode"
				:organization="requiredOrganization"
				:email="email"
			/>
			<template v-else>
				<c-callout v-if="logoutReason != null" type="error">
					<p>{{ logoutReason }}</p>
				</c-callout>
				<div v-else-if="requiredUser" class="text-center bg-harriet p-3 mb-2">
					<p>Please log in as {{ requiredUser }} to access this entry.</p>
				</div>
				<div v-else-if="requiredOrganizationName" class="text-center bg-harriet p-3 mb-2">
					<p>Please log in as a user of {{ requiredOrganizationName }} to access this form.</p>
				</div>
				<div v-else-if="destinationOrganizationName" class="text-center bg-harriet p-3 mb-2">
					<p>Please log in as a user of {{ destinationOrganizationName }} to access this page.</p>
				</div>
				<div v-if="verifyEmail" class="text-center bg-harriet p-3 mb-2">
					<p>Please log in to verify your email address.</p>
				</div>
				<div v-if="disableAuthenticationError" class="text-center bg-harriet p-3 mb-2">
					<p>This link is not valid.</p>
				</div>

				<external-auth-button
					v-if="!requiredAuthProvider || requiredAuthProvider === 'Google'"
					ref="google"
					:provider="requiredAuthProvider || 'Google'"
					:button-text="buttonPrefix + ' with Google'"
					authentication-action="login"
					:return-url="returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Google'"
					:required-org="requiredOrganization"
					:required-org-name="requiredOrganizationName"
					:destination-org-name="destinationOrganizationName"
					:required-user="requiredUser"
					:logout-reason="logoutReasonValue"
					:status="authStore.googleSubmitStatus"
					@click.native="toggleGoogleSubmit"
					@authenticated="cognitoSocialLogin($event, 'Google')"
					@cancelled="resetButtonStatuses"
				/>

				<MissingAccountCallout
					v-if="socialEmail && showNoAccountError && authenticationProvider === 'Google'"
					:email-address="socialEmail"
					:prompt-signup="!destinationOrganizationName"
					@signup="cognitoSocialSignup"
				/>

				<external-auth-button
					v-if="!requiredAuthProvider || requiredAuthProvider === 'Facebook'"
					ref="facebook"
					:provider="requiredAuthProvider || 'Facebook'"
					:button-text="buttonPrefix + ' with Facebook'"
					authentication-action="login"
					:return-url="returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Facebook'"
					:required-org="requiredOrganization"
					:required-org-name="requiredOrganizationName"
					:destination-org-name="destinationOrganizationName"
					:required-user="requiredUser"
					:logout-reason="logoutReasonValue"
					:status="authStore.facebookSubmitStatus"
					@click.native="toggleFacebookSubmit"
					@authenticated="cognitoSocialLogin($event, 'Facebook')"
					@cancelled="resetButtonStatuses"
				/>

				<MissingAccountCallout
					v-if="socialEmail && showNoAccountError && authenticationProvider === 'Facebook'"
					:email-address="socialEmail"
					:prompt-signup="!destinationOrganizationName"
					@signup="cognitoSocialSignup"
				/>

				<external-auth-button
					v-if="!requiredAuthProvider || requiredAuthProvider === 'Microsoft'"
					ref="microsoft"
					:provider="requiredAuthProvider || 'Microsoft'"
					:button-text="buttonPrefix + ' with Microsoft'"
					authentication-action="login"
					:return-url="returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Microsoft'"
					:required-org="requiredOrganization"
					:required-org-name="requiredOrganizationName"
					:destination-org-name="destinationOrganizationName"
					:required-user="requiredUser"
					:logout-reason="logoutReasonValue"
					:status="authStore.microsoftSubmitStatus"
					@click.native="toggleMicrosoftSubmit"
					@authenticated="cognitoSocialLogin($event, 'Microsoft')"
					@cancelled="resetButtonStatuses"
				/>

				<MissingAccountCallout
					v-if="socialEmail && showNoAccountError && authenticationProvider === 'Microsoft'"
					:email-address="socialEmail"
					:prompt-signup="!destinationOrganizationName"
					@signup="cognitoSocialSignup"
				/>

				<external-auth-button
					v-if="requiredAuthProvider === 'OpenIdConnect'"
					ref="openidconnect"
					:provider="requiredAuthProvider || 'OpenIdConnect'"
					:button-text="buttonPrefix + ' with SSO'"
					authentication-action="login"
					:return-url="returnUrl"
					:invite-code="inviteCode"
					:auth-method-type="requiredAuthMethod || 'Custom'"
					:required-org="requiredOrganization"
					:required-org-name="requiredOrganizationName"
					:destination-org-name="destinationOrganizationName"
					:required-user="requiredUser"
					:logout-reason="logoutReasonValue"
					:status="authStore.openIdConnectSubmitStatus"
					use-redirect
					@click.native="toggleOpenIdConnectSubmit"
					@authenticated="cognitoSocialLogin($event, 'OpenIdConnect')"
					@cancelled="resetButtonStatuses"
				/>

				<MissingAccountCallout
					v-if="socialEmail && showNoAccountError && authenticationProvider === 'OpenIdConnect'"
					:email-address="socialEmail"
					:prompt-signup="!destinationOrganizationName"
					@signup="cognitoSocialSignup"
				/>

				<template v-if="!requiredAuthProvider">
					<div class="strike">
						<span>or</span>
					</div>

					<c-email-login
						ref="loginForm"
						:prefilled-email="prefilledEmail || invitedEmail || verifyEmail || requiredUser"
						:status="authStore.emailSubmitStatus"
						:button-name="buttonPrefix"
						:show-no-account-error="showNoAccountError && authenticationProvider === 'cognito'"
						:show-password-error="showPasswordError"
						:show-mfa-error="authStore.mfa.locked"
						:required-org="requiredOrganizationName || destinationOrganizationName"
						:required-user="requiredUser"
						:show-invalid-org-message="showInvalidOrgMessage"
						:show-invalid-user-message="showInvalidUserMessage"
						@submitted="cognitoLogin"
						@email-changed="emailChanged"
					>
						<template #captcha>
							<slot name="captcha" />
						</template>
					</c-email-login>
				</template>

				<div class="sz-2 text-center">
					<p v-if="!requiredOrganization"><router-link :to="{ name: 'signup', query: { 'authenticationProvider': requiredAuthProvider }, params: { requiredAuthMethod }}" title="Sign up for Cognito Forms">Sign up</router-link> to create a new account.</p>
					<p v-if="!requiredAuthProvider"><router-link to="/forgot-password" title="Forgot your password? Follow to reset it">Forgot password?</router-link></p>
				</div>
			</template>
		</section>
	</transition>
</template>

<script>
	import ExternalAuthButton from 'src/components/auth/ExternalAuthButton.vue';
	import { emailLogin, socialLogin } from '@cognitoforms/api/services/authentication';
	import CCallout from '@cognitoforms/shared-components/src/components/content/Callout.vue';
	import CEmailLogin from 'src/components/auth/EmailLogin.vue';
	import { isWithinFrame } from '@cognitoforms/utils/utilities';
	import { authStore } from 'src/stores/auth-store';
	import MfaLoginProcess from 'src/components/auth/MfaLoginProcess.vue';
	import DeviceVerificationLoginProcess from 'src/components/auth/DeviceVerificationLoginProcess.vue';
	import MissingAccountCallout from './MissingAccountCallout.vue';

	export default {
		components: {
			ExternalAuthButton,
			CCallout,
			CEmailLogin,
			MfaLoginProcess,
			MissingAccountCallout,
			DeviceVerificationLoginProcess
		},
		props: {
			invitation: { type: Object, default: null },
			prefilledEmail: { type: String, default: '' },
			verifyEmail: { type: String, default: '' },
			verificationCode: { type: String, default: '' },
			requiredAuthMethod: { type: [String, Number], default: null },
			user: { type: String, default: null },
			disableAuthenticationError: { type: Boolean, default: false }
		},
		data() {
			return {
				showNoAccountError: this.$route.query.userExists === 'false',
				showPasswordError: false,
				authenticationProvider: this.$route.query.authenticationProvider,
				authenticationToken: null,
				socialSignUpLock: false,
				showInvalidOrgMessage: this.$route.query.invalidOrg === 'true',
				showInvalidUserMessage: this.$route.query.invalidUser === 'true',
				internalUser: this.user ? JSON.parse(this.user) : null,
				authStore,
				returnUrl: this.$route.query.returnUrl ? this.$route.query.returnUrl + (this.$route.hash || '') : null,
				emailRememberMe: false,
				email: null
			};
		},
		head() {
			return {
				title: 'Login',
				meta: [
					{
						name: 'description',
						content: 'Cognito Forms, a free online form builder that helps you collect information and payments. Easily create feedback forms, payment forms, registration forms, and much more.'
					}
				]
			};
		},
		computed: {
			buttonPrefix() {
				return !this.inviteCode ? 'Log In' : 'Join';
			},
			inviteCode() {
				return this.invitation ? this.invitation.inviteCode : null;
			},
			invitedEmail() {
				return this.invitation ? this.invitation.InvitedEmail : null;
			},
			logoutReason() {
				const logoutMessages = {
					TokenTimeout: 'You\'ve been inactive for a while. Please log in again.',
					KeepAliveTokenTimeout: 'You\'ve been inactive for a while. Please log in again.',
					StaleCredentials: 'You updated your email or password. Please log in again.',
					NoOrgs: 'You are no longer a member of any organizations. Please log in to create a new organization.',
					RemovedFromOrg: 'You\'ve been removed from your current organization. Please log in again.',
					MfaRequired: 'This organization requires two-factor authentication.',
					UniversalLogout: 'You\'ve been logged out. Please log in again.'
				};

				return logoutMessages[this.logoutReasonValue];
			},
			logoutReasonValue() {
				return this.$route.query.logoutReason || this.$route.query.logoutreason;
			},
			isTokenTimeout() {
				return this.logoutReasonValue === 'TokenTimeout' || this.logoutReasonValue === 'KeepAliveTokenTimeout';
			},
			requiredOrganizationName() {
				return this.$route.query.requiredOrganization;
			},
			destinationOrganizationName() {
				return this.$route.query.destinationOrganization;
			},
			requiredOrganization() {
				return this.$route.query.code;
			},
			requiredUser() {
				return this.$route.query.requiredUser;
			},
			socialEmail() {
				return this.internalUser ? this.internalUser.Email : null;
			},
			requiredAuthProvider() {
				if (this.requiredAuthMethod && this.requiredAuthMethod !== 'Unknown') {
					return this.$route.query.authenticationProvider;
				}
				return null;
			}
		},
		mounted() {
			if (process.env.NODE_ENV === 'development' && !isWithinFrame())
				import('src/components/auth/dev-login').then((x) => x.enableOldDevLogin(this));
		},
		methods: {
			emailChanged(email) {
				this.$emit('emailCaptured', email);
			},
			toggleGoogleSubmit() {
				this.authStore.googleSubmitStatus = 'validating';
			},
			toggleFacebookSubmit() {
				this.authStore.facebookSubmitStatus = 'validating';
			},
			toggleMicrosoftSubmit() {
				this.authStore.microsoftSubmitStatus = 'validating';
			},
			toggleOpenIdConnectSubmit() {
				this.authStore.openIdConnectSubmitStatus = 'validating';
			},
			resetButtonStatuses() {
				this.authStore.emailSubmitStatus = this.authStore.googleSubmitStatus = this.authStore.facebookSubmitStatus = this.authStore.microsoftSubmitStatus = this.authStore.openIdConnectSubmitStatus = 'default';
			},
			async cognitoLogin({ email, password, rememberMe }) {
				this.emailRememberMe = rememberMe;
				this.showNoAccountError = false;
				this.authenticationProvider = 'cognito';
				this.authStore.emailSubmitStatus = 'validating';
				const deviceToken = localStorage.getItem('deviceToken');

				emailLogin(email, password, rememberMe, this.inviteCode, this.verificationCode, this.requiredOrganization, this.returnUrl, deviceToken)
					.then(async res => {
						if (res.Succeeded) {
							return window.location.assign(res.ReturnUrl);
						}
						else if (res.RedirectUri)
							return window.location.assign(res.RedirectUri);

						if (res.MfaToken) {
							this.authStore.mfa.multifactorToken = res.MfaToken;
							this.authStore.mfa.backupEmail = res.MfaBackupEmail;
							this.authStore.mfa.encryptedAuthMethod = res.EncryptedAuthMethod;
							return;
						}
						else if (res.DeviceVerificationToken) {
							this.email = res.User?.Email;
							this.authStore.deviceVerification.token = res.DeviceVerificationToken;
						}

						if (res.InvalidOrg)
							this.showInvalidOrgMessage = true;
						else if (res.AccountExists)
							this.showPasswordError = true;
						else
							this.showNoAccountError = true;

						this.emailChanged(email);
						this.resetButtonStatuses();
					})
					.catch(error => {
						if (error.statusCode === 429) {
							this.showPasswordError = false;
							this.authStore.mfa.locked = true;
						}

						this.resetButtonStatuses();
					});
			},
			cognitoSocialLogin(authenticationToken, provider) {
				this.showNoAccountError = false;
				this.authenticationProvider = provider;
				this.authenticationToken = authenticationToken;
				const deviceToken = localStorage.getItem('deviceToken');

				socialLogin(provider, authenticationToken, this.inviteCode, this.requiredOrganization, this.requiredUser, this.returnUrl, deviceToken)
					.then(res => {
						const handleError = () => {
							this.internalUser = res.User;
							this.emailChanged(this.socialEmail);
							this.resetButtonStatuses();
						};

						if (res.Succeeded) {
							return window.location.assign(res.ReturnUrl);
						}
						else if (res.RedirectUri)
							return window.location.assign(res.RedirectUri);

						if (res.MfaToken) {
							this.authStore.mfa.multifactorToken = res.MfaToken;
							this.authStore.mfa.backupEmail = res.MfaBackupEmail;
							this.authStore.mfa.encryptedAuthMethod = res.EncryptedAuthMethod;
							return;
						}

						if (res.InvalidOrg) {
							this.showInvalidOrgMessage = true;
							handleError();
							return;
						}

						if (res.InvalidUser) {
							this.showInvalidUserMessage = true;
							handleError();
							return;
						}

						handleError();
						this.showNoAccountError = true;
					})
					.catch(error => {
						if (error.statusCode === 429) {
							this.showPasswordError = false;
							this.authStore.mfa.locked = true;
						}

						this.resetButtonStatuses();
					});
			},
			cognitoSocialSignup() {
				this.$router.push({
					name: 'signup', params: { social: true, autoSignupProvider: this.authenticationProvider }
				});
			}
		}
	};
</script>